import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { ticketTypeAvailable, salesStartInFuture } from './WebOrderForm';
import ChooseSeatedAddOns from './ChooseSeatedAddOns';
import ChooseSeatedTicketTypes from './ChooseSeatedTicketTypes';
import ChooseDonation from './ChooseDonation';
import CalendarEventInterestForm from './CalendarEventInterestForm';
import { upcomingPriceText } from '../utils/upcomingPrice';
import { trackXPixelEvent, X_PIXEL_ADD_TO_CART } from '../utils/xPixel';
import { priceOptionGroupForItem, quantityOptionsWithinOrderLimit, rowLabel } from '../utils/priceOptions';

const ticketTypeDisabled = (ticketType) => {
  return (
    ticketType.sales_ended || ticketType.sold_out || ticketType.sales_paused
  );
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const TicketTypeRow = ({
  ticketType,
  label,
  rows,
  promoCode,
  ticketTypes,
  formatItemsForGoogleTagManager,
  postToParent,
  updateTicketTypeQuantity,
  buildTicketReservation,
  csrfToken,
  addOns,
  ticketReservation,
  userContext,
  confirm,
  ticketGroups,
  findTicketGroup
}) => {
  return (
    ticketType ? (
      <tr className={(ticketTypeDisabled(ticketType) ? "disabled " : "") + "ticket-type-row"}>
        <td>
          <p className="mb-0">
            <span style={{fontWeight: "600"}}>
              {label}
            </span>
          </p>
          {ticketType.description ? (
            <div
              className="small"
              dangerouslySetInnerHTML={{ __html: ticketType.simple_format_description }}
            />
          ) : null}
        </td>
        <td>
          <p className="mb-0">
            {ticketType.free ? (
              <span style={{fontWeight: "600"}}>
                FREE
              </span>
            ) : (
              <span style={{fontWeight: "600"}}>
                {formatter.format(parseFloat(ticketType.calculated_price))}
              </span>
            )}
          </p>
          {confirm.enable_all_in_pricing && confirm.venue_ownership?.show_fee_breakdown_on_ticket_price !== false ? (
            <p className="mb-0 text-muted">
              {ticketType.all_in_price_description}
            </p>
          ) : null}
          {upcomingPriceText(ticketType, formatter) ? (
            <p className="mb-0 text-muted" data-testid={`upcoming-price-${ticketType.id}`}>
              {upcomingPriceText(ticketType, formatter)}
            </p>
          ) : null}
        </td>
        <td>
          {ticketType.sales_paused ? (
            <div className="col d-flex justify-content-end">
              <p className="mb-0"
                style={{"color": "#686868"}}>
                Sales paused
              </p>
            </div>
          ) : ticketType.scheduled ? (
            <div className="col d-flex justify-content-end">
              <p className="mb-0"
                style={{"color": "#686868"}}>
                {ticketType.wait_for_ticket_type && ticketType.wait_for_ticket_type.id ? (
                  <span>Sales not started</span>
                ) : (
                  <span>Sales start {ticketType.human_sales_start_date}</span>
                )}
              </p>
            </div>
          ) : ticketType.sales_ended ? (
            <div className="col d-flex justify-content-end">
              <p className="mb-0"
                style={{"color": "#686868"}}>
                Sale ended
              </p>
            </div>
          ) : ticketType.sold_out ? (
            <div className="col d-flex justify-content-end">
              <p className="mb-0"
                style={{"color": "#686868"}}>
                Sold out
              </p>
            </div>
          ) : (
            <div className="col-xs-auto d-flex justify-content-end"
                style={{"paddingRight": "15px"}}>
              <div className="form-group mb-0">
                <select className="form-control form-control-sm"
                        aria-label="Ticket Quantity"
                        onChange={
                          (e) => {
                            var quantity = parseInt(e.target.value);

                            if(quantity > 0){
                              var updatedItem = Object.assign({}, ticketType, {
                                quantity: quantity
                              });

                              var items = formatItemsForGoogleTagManager(
                                confirm,
                                promoCode,
                                [updatedItem],
                                []
                              );

                              postToParent("ga.add_to_cart", items)
                              trackXPixelEvent(X_PIXEL_ADD_TO_CART, confirm, {
                                value: updatedItem.calculated_price * quantity,
                                numItems: quantity
                              });

                              if (typeof window.rdt !== 'undefined') {
                                window.rdt('track', 'AddToCart', {
                                  currency: 'USD',
                                  value: updatedItem.calculated_price * quantity,
                                  itemCount: quantity,
                                  products: [{
                                    id: confirm.id.toString(),
                                    name: confirm.title,
                                    category: "Tickets"
                                  }]
                                });
                              }
                            }

                            updateTicketTypeQuantity({ ticketTypeId: ticketType.id, quantity });
                            
                            var updated = [...ticketTypes].map((tt) => {
                              if(tt.id === ticketType.id){
                                return Object.assign({}, tt, {quantity: quantity});
                              } else {
                                return tt;
                              }
                            });
                            buildTicketReservation(csrfToken, confirm, updated, promoCode, addOns, ticketReservation, userContext);
                          }
                        }
                        value={ticketType.quantity || 0}
                        style={{"border": "1px solid #f3f3f3"}}>
                  {quantityOptionsWithinOrderLimit(rows, ticketType).map((quantity, index) =>
                    <option key={index} value={quantity}>
                      {quantity}
                    </option>
                  )}
                </select>
              </div>
            </div>
          )}
        </td>
      </tr>
    ) : null
  );
}

const AddOnRow = ({
  team,
  csrfToken,
  confirm,
  ticketTypes,
  addOns,
  buildTicketReservation,
  visibleAddOns,
  promoCode,
  addOnsChanged,
  addOnQuantityOptions,
  ticketReservation,
  userContext,
  postToParent,
  formatItemsForGoogleTagManager,
  checkoutGroupItem,
  startAtAddOns
}) => {
  const addOn = visibleAddOns
    .find((ao) => ao.id === checkoutGroupItem.item_id);

  return (
    addOn ? (
      <tr className="add-on-row">
        <td>
          <div className="d-flex flex-column flex-lg-row">
            {addOn.image_thumb_url && (
              <div style={{"flexShrink": "0"}}>
                <div style={{
                  "width": "80px",
                  "height": "80px",
                  "border": "1px solid #E9EAEB",
                  "borderRadius": "8px",
                  "overflow": "hidden",
                  "backgroundColor": "#ffffff",
                  "marginRight": "15px",
                  "marginBottom": "5px"
                }}>
                  <img
                    src={addOn.image_thumb_url}
                    alt={addOn.name}
                    style={{
                      "width": "100%",
                      "height": "100%",
                      "objectFit": "cover"
                    }}
                  />
                </div>
              </div>
            )}
            <div className="flex-grow-1">
              <p className="mb-0">
                <span style={{fontWeight: "600"}}>
                  {addOn.name}
                </span>
              </p>
              {addOn.description ? (
                <div
                  className="small"
                  dangerouslySetInnerHTML={{ __html: addOn.simple_format_description }}
                />
              ) : null}
            </div>
          </div>
        </td>
        <td>
          <p className="mb-0">
            <span style={{fontWeight: "600"}}>
              {formatter.format(parseFloat(addOn.calculated_price))}
            </span>
          </p>
          {confirm.enable_all_in_pricing && confirm.venue_ownership?.show_fee_breakdown_on_ticket_price !== false ? (
            <p className="mb-0 text-muted">
              {addOn.all_in_price_description}
            </p>
          ) : null}
        </td>
        <td>
          {(addOn.available_tickets == 0 || addOn.sold_out) ? (
            <div className="col d-flex justify-content-end">
              <p className="mb-0"
                style={{"color": "#686868"}}>
                Sold out
              </p>
            </div>
          ) : (
            <div className="col-xs-auto d-flex justify-content-end"
                style={{"paddingRight": "15px"}}>
              <div className="form-group mb-0">
                <select className="form-control form-control-sm"
                        aria-label="Add-on Quantity"
                        onChange={
                          (e) => {
                            var quantity = parseInt(e.target.value);

                            var updated = [...addOns].map((ao) => {
                              if(ao.id === addOn.id){
                                return Object.assign({}, ao, {quantity: quantity});
                              } else {
                                return ao;
                              }
                            });

                            if(quantity > 0){
                              var updatedItem = Object.assign({}, addOn, {
                                quantity: quantity
                              });

                              var items = formatItemsForGoogleTagManager(
                                confirm,
                                promoCode,
                                [],
                                [updatedItem]
                              );

                              postToParent("ga.add_to_cart", items)
                            }

                            addOnsChanged(updated);
                            buildTicketReservation(csrfToken, confirm, ticketTypes, promoCode, updated, ticketReservation, userContext);
                          }
                        }
                        value={addOn.quantity || 0}
                        style={{"border": "1px solid #f3f3f3"}}>
                  {addOnQuantityOptions(addOn, ticketTypes, userContext, startAtAddOns).map((quantity, index) =>
                    <option key={index} value={quantity}>
                      {quantity}
                    </option>
                  )}
                </select>
              </div>
            </div>
          )}
        </td>
      </tr>
    ) : null
  );
};

const CheckoutGroupItemRow = ({
  team,
  checkoutGroupItem,
  checkoutGroupItems,
  visibleTickets,
  promoCode,
  ticketTypes,
  formatItemsForGoogleTagManager,
  postToParent,
  updateTicketTypeQuantity,
  buildTicketReservation,
  csrfToken,
  addOns,
  ticketReservation,
  userContext,
  confirm,
  addOnsChanged,
  addOnQuantityOptions,
  visibleAddOns,
  seatedAddOns,
  seatsIOPublicKey,
  selectedObjectLabel,
  selectedObjectType,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  onSelectionChange,
  no_orphan_seats,
  onProceed,
  submitting,
  visibleTicketTypes,
  announceListEnabled,
  waitlistEnabled,
  ticketTypesChanged,
  donationCustomChargeType,
  donationCustomChargeTypeChanged,
  debouncedBuildTicketReservation,
  feeRules,
  donationTitle,
  donationCoverProcessingText,
  ticketGroups,
  findTicketGroup,
  startAtAddOns,
  membershipProducts,
  selectedMembershipProduct,
  selectedMembershipProductChanged,
  hasBothCadences,
  getSelectedCadence,
  getActivePrice,
  handleCadenceChange,
}) => {
  return (
    checkoutGroupItem.item_type === "MembershipProduct" ? (
      (() => {
        const product = (membershipProducts || []).find((mp) => mp.id === checkoutGroupItem.item_id);
        if (!product) return null;

        const price = getActivePrice(product);
        const hasExisting = product.existing_membership != null;
        const hasBoth = hasBothCadences(product);
        const selected = selectedMembershipProduct && selectedMembershipProduct.id === product.id;

        return (
          <tr className="ticket-type-row">
            <td>
              <div className="d-flex flex-column flex-lg-row">
                {product.image_thumb_url && (
                  <div style={{"flexShrink": "0"}}>
                    <div style={{
                      "width": "80px",
                      "height": "80px",
                      "border": "1px solid #E9EAEB",
                      "borderRadius": "8px",
                      "overflow": "hidden",
                      "backgroundColor": "#ffffff",
                      "marginRight": "15px",
                      "marginBottom": "5px"
                    }}>
                      <img
                        src={product.image_thumb_url}
                        alt={product.name}
                        style={{
                          "width": "100%",
                          "height": "100%",
                          "objectFit": "cover"
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="flex-grow-1">
                  <p className="mb-0">
                    <span style={{fontWeight: "600"}}>
                      {product.name}
                    </span>
                  </p>
                  {product.description && product.description.length > 0 ? (
                    <p className="mb-0 text-muted small">{product.description}</p>
                  ) : null}
                </div>
              </div>
            </td>
            <td>
              {hasBoth && !hasExisting ? (
                <div className="d-flex flex-column">
                  <label className="d-flex align-items-center mb-1" style={{ cursor: "pointer", fontSize: "14px" }}>
                    <input
                      type="radio"
                      name={`cadence-${product.id}`}
                      value="year"
                      checked={getSelectedCadence(product) === "year"}
                      onChange={() => handleCadenceChange(product, "year")}
                      className="mr-1"
                    />
                    <span style={{fontWeight: "600"}}>{product.ticketing_price.price_to_currency}</span>
                    <span className="text-muted small ml-1">per year</span>
                  </label>
                  <label className="d-flex align-items-center mb-0" style={{ cursor: "pointer", fontSize: "14px" }}>
                    <input
                      type="radio"
                      name={`cadence-${product.id}`}
                      value="month"
                      checked={getSelectedCadence(product) === "month"}
                      onChange={() => handleCadenceChange(product, "month")}
                      className="mr-1"
                    />
                    <span style={{fontWeight: "600"}}>{product.ticketing_monthly_price.price_to_currency}</span>
                    <span className="text-muted small ml-1">per month</span>
                  </label>
                </div>
              ) : (
                <>
                  <p className="mb-0">
                    <span style={{fontWeight: "600"}}>
                      {price ? price.price_to_currency : ""}
                    </span>
                  </p>
                  {price ? (
                    <p className="mb-0 text-muted small">
                      per {price.interval_label}
                    </p>
                  ) : null}
                </>
              )}
            </td>
            <td>
              {hasExisting ? (
                <div className="col d-flex justify-content-end">
                  <span className="badge badge-success px-3 py-2" style={{ fontSize: "13px", borderRadius: "20px" }}>
                    <i className="fas fa-check-circle mr-1"></i>
                    Active Member
                  </span>
                </div>
              ) : (
                <div className="col-xs-auto d-flex justify-content-end"
                    style={{"paddingRight": "15px"}}>
                  <div className="form-group mb-0">
                    <select className="form-control form-control-sm"
                            aria-label="Membership Quantity"
                            onChange={(e) => {
                              if (parseInt(e.target.value) === 1) {
                                selectedMembershipProductChanged({ ...product, ticketing_price: price });
                              } else {
                                selectedMembershipProductChanged(null);
                              }
                            }}
                            value={selected ? "1" : "0"}
                            style={{"border": "1px solid #f3f3f3"}}>
                      <option value="0">0</option>
                      <option value="1">1</option>
                    </select>
                  </div>
                </div>
              )}
            </td>
          </tr>
        );
      })()
    ) : checkoutGroupItem.enable_donation ? (
      <tr>
        <td colSpan={3}>
          <ChooseDonation team={team}
                          csrfToken={csrfToken}
                          confirm={confirm}
                          buildTicketReservation={buildTicketReservation}
                          donationCustomChargeType={donationCustomChargeType}
                          donationCustomChargeTypeChanged={donationCustomChargeTypeChanged}
                          ticketTypes={ticketTypes}
                          addOns={addOns}
                          promoCode={promoCode}
                          userContext={userContext}
                          debouncedBuildTicketReservation={debouncedBuildTicketReservation}
                          feeRules={feeRules}
                          donationTitle={confirm && confirm.donation_title ? confirm.donation_title : "Make a Donation"}
                          donationCoverProcessingText={confirm && confirm.donation_cover_processing_text ? confirm.donation_cover_processing_text : `I'll cover processing costs so that my full donation goes to ${confirm.organizer_name}`}
                          marginBottom={"0px"}
                          ticketReservation={ticketReservation} />
        </td>
      </tr>
    ) : checkoutGroupItem.seatsio_event_id && checkoutGroupItem.seatsio_event_id.length > 0 ? (
      <tr>
        <td colSpan={3}>
          <ChooseSeatedTicketTypes team={team}
                                    csrfToken={csrfToken}
                                    confirm={confirm}
                                    ticketTypes={ticketTypes}
                                    promoCode={promoCode}
                                    ticketTypesChanged={ticketTypesChanged}
                                    buildTicketReservation={buildTicketReservation}
                                    userContext={userContext}
                                    addOns={addOns}
                                    ticketReservation={ticketReservation}
                                    postToParent={postToParent}
                                    formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                    seatsIOPublicKey={seatsIOPublicKey}
                                    seatsIOChart={seatsIOChart}
                                    seatsIOChartChanged={seatsIOChartChanged}
                                    seatsIOSelectedObjects={seatsIOSelectedObjects}
                                    seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
                                    selectedObjectLabel={selectedObjectLabel}
                                    selectedObjectType={selectedObjectType}
                                    visibleTicketTypes={visibleTicketTypes}
                                    onSelectionChange={onSelectionChange}
                                    ownsUnavailableState={false}
                                    no_orphan_seats={no_orphan_seats}
                                    onProceed={onProceed}
                                    submitting={submitting} />
        </td>
      </tr>
    ) : checkoutGroupItem.add_on_seatsio_event_id && checkoutGroupItem.add_on_seatsio_event_id.length > 0 && seatedAddOns.length > 0 ? (
      visibleAddOns.length > 0 ? (
        <tr>
          <td colSpan={3}>
            <ChooseSeatedAddOns team={team}
                                csrfToken={csrfToken}
                                confirm={confirm}
                                ticketTypes={ticketTypes}
                                addOns={addOns}
                                addOnsChanged={addOnsChanged}
                                buildTicketReservation={buildTicketReservation}
                                promoCode={promoCode}
                                ticketReservation={ticketReservation}
                                userContext={userContext}
                                postToParent={postToParent}
                                formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                seatsIOPublicKey={seatsIOPublicKey}
                                selectedObjectLabel={selectedObjectLabel}
                                selectedObjectType={selectedObjectType}
                                visibleAddOns={visibleAddOns} />
          </td>
        </tr>
      ) : null
    ) : checkoutGroupItem.item_type === "SeatingChartEvent" && checkoutGroupItem.extra.seatsio_event_id && checkoutGroupItem.extra.seatsio_event_id.length > 0 && seatedAddOns.length > 0 ? (
      visibleAddOns.length > 0 ? (
        <tr>
          <td colSpan={3}>
            <ChooseSeatedAddOns team={team}
                                csrfToken={csrfToken}
                                confirm={confirm}
                                ticketTypes={ticketTypes}
                                addOns={addOns}
                                addOnsChanged={addOnsChanged}
                                buildTicketReservation={buildTicketReservation}
                                promoCode={promoCode}
                                ticketReservation={ticketReservation}
                                userContext={userContext}
                                postToParent={postToParent}
                                formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                seatsIOPublicKey={seatsIOPublicKey}
                                selectedObjectLabel={selectedObjectLabel}
                                selectedObjectType={selectedObjectType}
                                checkoutGroupItem={checkoutGroupItem}
                                visibleAddOns={visibleAddOns} />
          </td>
        </tr>
      ) : null
    ) : checkoutGroupItem.item_type === "TicketType" ? (
      (() => {
        const priceGroup = priceOptionGroupForItem(checkoutGroupItem, checkoutGroupItems || [checkoutGroupItem], visibleTickets || []);
        if (!priceGroup) return null;
        const { heading, rows } = priceGroup;

        return (
          <>
            {heading ? (
              <tr className="ticket-type-row">
                <td colSpan={3}>
                  <p className="mb-0" data-testid="price-option-heading">
                    <span style={{fontWeight: "600"}}>{heading}</span>
                  </p>
                </td>
              </tr>
            ) : null}
            {rows.map((ticketType) =>
              <TicketTypeRow key={ticketType.id}
                            ticketType={ticketType}
                            label={heading ? rowLabel(heading, ticketType) : ticketType.name}
                            rows={rows}
                            promoCode={promoCode}
                            ticketTypes={ticketTypes}
                            formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                            postToParent={postToParent}
                            updateTicketTypeQuantity={updateTicketTypeQuantity}
                            buildTicketReservation={buildTicketReservation}
                            csrfToken={csrfToken}
                            addOns={addOns}
                            ticketReservation={ticketReservation}
                            userContext={userContext}
                            ticketGroups={ticketGroups}
                            findTicketGroup={findTicketGroup}
                            confirm={confirm} />
            )}
          </>
        );
      })()
    ) : checkoutGroupItem.item_type === "AddOn" ? (
      <AddOnRow team={team}
                csrfToken={csrfToken}
                confirm={confirm}
                ticketTypes={ticketTypes}
                addOns={addOns}
                buildTicketReservation={buildTicketReservation}
                visibleAddOns={visibleAddOns}
                promoCode={promoCode}
                addOnsChanged={addOnsChanged}
                addOnQuantityOptions={addOnQuantityOptions}
                ticketReservation={ticketReservation}
                userContext={userContext}
                postToParent={postToParent}
                formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                checkoutGroupItem={checkoutGroupItem}
                startAtAddOns={startAtAddOns} />
    ) :null
  );
}

CheckoutGroupItemRow.propTypes = {
  checkoutGroupItem: PropTypes.object.isRequired,
  team: PropTypes.object,
  checkoutGroupItems: PropTypes.array,
  visibleTickets: PropTypes.array,
  promoCode: PropTypes.object,
  ticketTypes: PropTypes.array,
  formatItemsForGoogleTagManager: PropTypes.func,
  postToParent: PropTypes.func,
  updateTicketTypeQuantity: PropTypes.func,
  buildTicketReservation: PropTypes.func,
  csrfToken: PropTypes.string,
  addOns: PropTypes.array,
  ticketReservation: PropTypes.object,
  userContext: PropTypes.object,
  confirm: PropTypes.object,
  addOnsChanged: PropTypes.func,
  addOnQuantityOptions: PropTypes.func,
  visibleAddOns: PropTypes.array,
  seatedAddOns: PropTypes.array,
  seatsIOPublicKey: PropTypes.string,
  selectedObjectLabel: PropTypes.func,
  selectedObjectType: PropTypes.func,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func,
  onSelectionChange: PropTypes.func,
  no_orphan_seats: PropTypes.bool,
  onProceed: PropTypes.func,
  submitting: PropTypes.bool,
  visibleTicketTypes: PropTypes.func,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  ticketTypesChanged: PropTypes.func,
  donationCustomChargeType: PropTypes.object,
  donationCustomChargeTypeChanged: PropTypes.func,
  debouncedBuildTicketReservation: PropTypes.func,
  feeRules: PropTypes.array,
  donationTitle: PropTypes.string,
  donationCoverProcessingText: PropTypes.string,
  ticketGroups: PropTypes.object,
  findTicketGroup: PropTypes.func,
  startAtAddOns: PropTypes.bool,
  membershipProducts: PropTypes.array,
  selectedMembershipProduct: PropTypes.object,
  selectedMembershipProductChanged: PropTypes.func,
  hasBothCadences: PropTypes.func,
  getSelectedCadence: PropTypes.func,
  getActivePrice: PropTypes.func,
  handleCadenceChange: PropTypes.func,
};

CheckoutGroupItemRow.defaultProps = {
  team: null,
  checkoutGroupItems: null,
  visibleTickets: null,
  promoCode: null,
  ticketTypes: null,
  formatItemsForGoogleTagManager: null,
  postToParent: null,
  updateTicketTypeQuantity: null,
  buildTicketReservation: null,
  csrfToken: null,
  addOns: null,
  ticketReservation: null,
  userContext: null,
  confirm: null,
  addOnsChanged: null,
  addOnQuantityOptions: null,
  visibleAddOns: null,
  seatedAddOns: null,
  seatsIOPublicKey: null,
  selectedObjectLabel: null,
  selectedObjectType: null,
  seatsIOChart: null,
  seatsIOChartChanged: null,
  seatsIOSelectedObjects: null,
  seatsIOSelectedObjectsChanged: null,
  onSelectionChange: null,
  no_orphan_seats: null,
  onProceed: null,
  submitting: null,
  visibleTicketTypes: null,
  announceListEnabled: null,
  waitlistEnabled: null,
  ticketTypesChanged: null,
  donationCustomChargeType: null,
  donationCustomChargeTypeChanged: null,
  debouncedBuildTicketReservation: null,
  feeRules: null,
  donationTitle: null,
  donationCoverProcessingText: null,
  ticketGroups: null,
  findTicketGroup: null,
  startAtAddOns: null,
  membershipProducts: null,
  selectedMembershipProduct: null,
  selectedMembershipProductChanged: null,
  hasBothCadences: null,
  getSelectedCadence: null,
  getActivePrice: null,
  handleCadenceChange: null,
};

TicketTypeRow.propTypes = {
  ticketType: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  ticketTypes: PropTypes.array,
  formatItemsForGoogleTagManager: PropTypes.func,
  postToParent: PropTypes.func,
  updateTicketTypeQuantity: PropTypes.func,
  buildTicketReservation: PropTypes.func,
  csrfToken: PropTypes.string,
  addOns: PropTypes.array,
  ticketReservation: PropTypes.object,
  userContext: PropTypes.object,
  confirm: PropTypes.object.isRequired,
  ticketGroups: PropTypes.object,
  findTicketGroup: PropTypes.func,
};

TicketTypeRow.defaultProps = {
  promoCode: null,
  ticketTypes: null,
  formatItemsForGoogleTagManager: null,
  postToParent: null,
  updateTicketTypeQuantity: null,
  buildTicketReservation: null,
  csrfToken: null,
  addOns: null,
  ticketReservation: null,
  userContext: null,
  ticketGroups: null,
  findTicketGroup: null,
};

const ChooseCheckoutCategories = ({
  team,
  csrfToken,
  confirm,
  ticketTypes,
  promoCode,
  updateTicketTypeQuantity,
  buildTicketReservation,
  visibleTicketTypes,
  addOns,
  ticketReservation,
  postToParent,
  formatItemsForGoogleTagManager,
  userContext,
  announceListEnabled,
  waitlistEnabled,
  checkoutCategories,
  restrictedTicketTypeId,
  activeCategory,
  setActiveCategory,
  addOnsChanged,
  addOnQuantityOptions,
  visibleAddOns,
  seatedAddOns,
  seatsIOPublicKey,
  selectedObjectLabel,
  selectedObjectType,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  onSelectionChange,
  no_orphan_seats,
  onProceed,
  submitting,
  ticketTypesChanged,
  donationCustomChargeType,
  donationCustomChargeTypeChanged,
  debouncedBuildTicketReservation,
  feeRules,
  donationTitle,
  donationCoverProcessingText,
  ticketGroups,
  findTicketGroup,
  startAtAddOns,
  membershipProducts,
  selectedMembershipProduct,
  selectedMembershipProductChanged,
  hasResaleListings,
}) => {
  const [availibleTickets, setAvailibleTickets] = useState([]);
  const [visibleTickets, setVisibleTickets] = useState([]);
  const [scheduledTickets, setScheduledTickets] = useState([]);
  const anyVisibleTicketTypePaused = visibleTickets.some((ticketType) => ticketType.sales_paused);
  const [selectedCadences, setSelectedCadences] = useState({});

  const hasBothCadences = (product) => {
    return product.ticketing_price && product.ticketing_monthly_price;
  };

  const getSelectedCadence = (product) => {
    if (selectedCadences[product.id]) return selectedCadences[product.id];
    if (!product.ticketing_price && product.ticketing_monthly_price) return "month";
    return "year";
  };

  const getActivePrice = (product) => {
    const cadence = getSelectedCadence(product);
    if (cadence === "month" && product.ticketing_monthly_price) {
      return product.ticketing_monthly_price;
    }
    return product.ticketing_price || product.ticketing_monthly_price;
  };

  const handleCadenceChange = (product, cadence) => {
    setSelectedCadences(prev => ({ ...prev, [product.id]: cadence }));
    const isSelected = selectedMembershipProduct && selectedMembershipProduct.id === product.id;
    if (isSelected) {
      const price = cadence === "month" && product.ticketing_monthly_price
        ? product.ticketing_monthly_price
        : product.ticketing_price;
      selectedMembershipProductChanged({ ...product, ticketing_price: price });
    }
  };

  useEffect(() => {
    setVisibleTickets(
      visibleTicketTypes(confirm, ticketTypes, promoCode)
    );
  }, [ticketTypes, promoCode, visibleTicketTypes]);

  useEffect(() => {
    setAvailibleTickets(
      visibleTickets.filter((ticketType) => ticketTypeAvailable(ticketType))
    );

    setScheduledTickets(
      visibleTickets
        .filter((ticketType) => ticketType.scheduled)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    );
  }, [visibleTickets]);

  const checkoutGroupHasVisibleItem = (checkoutGroup) => {
    return checkoutGroup.checkout_group_items.some((checkoutGroupItem) => {
      switch (checkoutGroupItem.item_type) {
        case 'TicketType':
          return visibleTickets.find((tt) => tt.id === checkoutGroupItem.item_id) !== undefined;
        case 'AddOn':
          return visibleAddOns.find((ao) => ao.id === checkoutGroupItem.item_id) !== undefined;
        case 'MembershipProduct':
          return (membershipProducts || []).find((mp) => mp.id === checkoutGroupItem.item_id) !== undefined;
        default:
          return (
            checkoutGroupItem.seatsio_event_id
              || checkoutGroupItem.add_on_seatsio_event_id
              || checkoutGroupItem.enable_donation
              || checkoutGroupItem.item_type === "SeatingChartEvent"
          );
      }
    });
  }

  const categoryHasVisibleItem = (checkoutCategory) =>
    checkoutCategory.checkout_groups.some(checkoutGroupHasVisibleItem);

  const visibleCheckoutCategories = restrictedTicketTypeId
    ? checkoutCategories.filter(categoryHasVisibleItem)
    : checkoutCategories;

  const showCheckoutGroup = (checkoutCategory, checkoutGroup) => {
    return (
      activeCategory.id === checkoutCategory.id
        && checkoutGroupHasVisibleItem(checkoutGroup)
    );
  }

  return (
    <div>
      <ul className="nav inverse-nav-tabs px-3 bottom-border"
          style={{"marginBottom": "20px"}}>
        {visibleCheckoutCategories.map((category, index) =>
          <li key={index}
              className="nav-item"
              style={{"marginBottom": "-2px"}}>
            <a className={"nav-link pb-3 " + (activeCategory.id === category.id ? "active" : "")}
                onClick={
                  (e) => {
                    e.preventDefault();
                    setActiveCategory(category);
                  }
                }
                href="#">
              <strong>{category.name}</strong>
            </a>
          </li>
        )}
      </ul>
      {availibleTickets.length === 0 && !confirm.seated_hold_present && !hasResaleListings ? (
        <div style={{"marginBottom": "20px"}}>
          {confirm.preselling ? null : salesStartInFuture(confirm) ? (
            announceListEnabled ? (
              <CalendarEventInterestForm
                confirm={confirm}
                csrfToken={csrfToken}
                interestType="Announce"
              />
            ) : (
              <div className="card border-0 h-100" style={{"background": "#f9f9f9"}}>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <p className="mb-2"
                      style={{"fontSize": "16px"}}>
                      <strong>Coming soon!</strong>
                    </p>
                    <p className="mb-0">
                      Sales not started yet.
                    </p>
                  </div>
                </div>
              </div>
            )
          ) : (
            waitlistEnabled && !anyVisibleTicketTypePaused ? (
              <CalendarEventInterestForm
                confirm={confirm}
                csrfToken={csrfToken}
                interestType="Waitlist"
              />
            ) : (
              <div className="card border-0 h-100"
                    style={{"background": "#f9f9f9"}}>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <p className="mb-2"
                        style={{"fontSize": "16px"}}>
                      <strong>Tickets not currently available</strong>
                    </p>
                    {anyVisibleTicketTypePaused ? (
                      <p className="mb-0">
                        Online sales for this event are paused.
                      </p>
                    ) : scheduledTickets.length > 0 ? (
                      <>
                        <p className="mb-0">
                          Online sales for this event are currently not available.
                        </p>
                        <p className="mb-0">
                          {`The next sale will begin on ${scheduledTickets[0].human_start_time}.`}
                        </p>
                      </>
                    ) : (
                      <p className="mb-0">
                        Online sales for this event have ended.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      ): null}
      {checkoutCategories.map((category) =>
        category.checkout_groups.map((checkoutGroup, index) =>
          <div key={index}
                style={{
                  "border": "1px solid #ECECEC",
                  "borderRadius": "8px"
                }}
                className={"card " + (showCheckoutGroup(category, checkoutGroup) ? "" : "d-none")}>
            <div className="card-header border-0">
              <p className='mb-0'
                  style={{"fontSize": "16px"}}>
                <strong>{checkoutGroup.name}</strong>
              </p>
            </div>
            {checkoutGroup.description && checkoutGroup.description.length > 0 ? (
              <div className='card-body pb-0'>
                <div className='small'
                      dangerouslySetInnerHTML={{__html: checkoutGroup.formatted_description}}>
                </div>
              </div>
            ) : null}
            <div className='card-body'>
              <table className='table table-sm vertical-middle mb-0 table-borderless'>
                <colgroup>
                  <col span={1} style={{"width": "55%"}} />
                  <col span={1} style={{"width": "25%"}} />
                  <col span={1} style={{"width": "20%"}} />
                </colgroup>
                <tbody>
                  {checkoutGroup.checkout_group_items.map((checkoutGroupItem, index) =>
                    <CheckoutGroupItemRow key={index}
                                          team={team}
                                          visibleTickets={visibleTickets}
                                          promoCode={promoCode}
                                          ticketTypes={ticketTypes}
                                          formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                                          postToParent={postToParent}
                                          updateTicketTypeQuantity={updateTicketTypeQuantity}
                                          buildTicketReservation={buildTicketReservation}
                                          csrfToken={csrfToken}
                                          addOns={addOns}
                                          ticketReservation={ticketReservation}
                                          userContext={userContext}
                                          confirm={confirm}
                                          addOnsChanged={addOnsChanged}
                                          addOnQuantityOptions={addOnQuantityOptions}
                                          visibleAddOns={visibleAddOns}
                                          seatedAddOns={seatedAddOns}
                                          seatsIOPublicKey={seatsIOPublicKey}
                                          selectedObjectLabel={selectedObjectLabel}
                                          selectedObjectType={selectedObjectType}
                                          seatsIOChart={seatsIOChart}
                                          seatsIOChartChanged={seatsIOChartChanged}
                                          seatsIOSelectedObjects={seatsIOSelectedObjects}
                                          seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
                                          onSelectionChange={onSelectionChange}
                                          no_orphan_seats={no_orphan_seats}
                                          onProceed={onProceed}
                                          submitting={submitting}
                                          visibleTicketTypes={visibleTicketTypes}
                                          announceListEnabled={announceListEnabled}
                                          waitlistEnabled={waitlistEnabled}
                                          ticketTypesChanged={ticketTypesChanged}
                                          donationCustomChargeType={donationCustomChargeType}
                                          donationCustomChargeTypeChanged={donationCustomChargeTypeChanged}
                                          debouncedBuildTicketReservation={debouncedBuildTicketReservation}
                                          feeRules={feeRules}
                                          donationTitle={donationTitle}
                                          donationCoverProcessingText={donationCoverProcessingText}
                                          ticketGroups={ticketGroups}
                                          findTicketGroup={findTicketGroup}
                                          checkoutGroupItem={checkoutGroupItem}
                                          checkoutGroupItems={checkoutGroup.checkout_group_items}
                                          startAtAddOns={startAtAddOns}
                                          membershipProducts={membershipProducts}
                                          selectedMembershipProduct={selectedMembershipProduct}
                                          selectedMembershipProductChanged={selectedMembershipProductChanged}
                                          hasBothCadences={hasBothCadences}
                                          getSelectedCadence={getSelectedCadence}
                                          getActivePrice={getActivePrice}
                                          handleCadenceChange={handleCadenceChange} />
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  )
};

ChooseCheckoutCategories.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  updateTicketTypeQuantity: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  visibleTicketTypes: PropTypes.func.isRequired,
  restrictedTicketTypeId: PropTypes.number,
  addOns: PropTypes.array,
  ticketReservation: PropTypes.object,
  postToParent: PropTypes.func.isRequired,
  formatItemsForGoogleTagManager: PropTypes.func.isRequired,
  userContext: PropTypes.object,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  checkoutCategories: PropTypes.array,
  addOnsChanged: PropTypes.func.isRequired,
  addOnQuantityOptions: PropTypes.func.isRequired,
  visibleAddOns: PropTypes.array,
  seatedAddOns: PropTypes.array,
  seatsIOPublicKey: PropTypes.string,
  selectedObjectLabel: PropTypes.func.isRequired,
  selectedObjectType: PropTypes.func.isRequired,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func,
  no_orphan_seats: PropTypes.bool.isRequired,
  ticketTypesChanged: PropTypes.func.isRequired,
  donationCustomChargeType: PropTypes.object,
  donationCustomChargeTypeChanged: PropTypes.func.isRequired,
  debouncedBuildTicketReservation: PropTypes.func,
  feeRules: PropTypes.array.isRequired,
  donationTitle: PropTypes.string,
  donationCoverProcessingText: PropTypes.string,
  ticketGroups: PropTypes.object.isRequired,
  findTicketGroup: PropTypes.func.isRequired,
  startAtAddOns: PropTypes.bool,
  membershipProducts: PropTypes.array,
  selectedMembershipProduct: PropTypes.object,
  selectedMembershipProductChanged: PropTypes.func,
  hasResaleListings: PropTypes.bool,
};

ChooseCheckoutCategories.defaultProps = {
  hasResaleListings: false,
};

export default ChooseCheckoutCategories;
